import { useCallback, useEffect, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  mosaicEngine,
  toMosaicView,
  type CellState,
  type MosaicAction,
  type MosaicSize,
  type MosaicState,
  type MosaicView,
  type SkillLevels,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO = 'solo'
const PLAYER = { id: SOLO, displayName: 'You', userId: null } as const

interface Round {
  readonly state: MosaicState
}

interface RoundEvent {
  readonly action: MosaicAction
  readonly now: number
}

function start(
  seed: string,
  size: MosaicSize,
  pictureKey: string,
  levels: SkillLevels,
  now: number,
): Round {
  return {
    state: mosaicEngine.createInitialState(
      { size, pictureKey, levels, startedAt: now },
      [PLAYER],
      createRng(seed),
    ),
  }
}

/** Pure, so React re-running it cannot record a round twice. */
function reduceRound(round: Round, event: RoundEvent): Round {
  const outcome = applyAction(mosaicEngine, round.state, event.action, {
    playerId: SOLO,
    now: event.now,
  })

  // A refused move leaves the round exactly as it was, here and on the server.
  return outcome.ok ? { state: outcome.state } : round
}

export interface Mosaic {
  readonly view: MosaicView
  /** Put a square in a state. */
  readonly set: (cell: number, state: CellState) => void
  /** Rub the whole thing out. */
  readonly clear: () => void
  readonly again: () => void
  readonly finished: boolean
  readonly milestone: number | null
}

export interface MosaicConfigFromRound {
  readonly size: MosaicSize
  readonly pictureKey: string
  readonly levels: SkillLevels
}

/**
 * Maritza's Mosaic, running the shared engine in the browser.
 *
 * The same shape as Stripey's, with one difference that matters: the round
 * carries **which picture** as well as the size. Every other game rebuilds its
 * round from the seed, and can, because what the seed feeds is code. A
 * mosaic's pictures are a hand-drawn list, and choosing from a list depends on
 * how long the list is — so a tenth picture added while she had a round open
 * would deal her a different one here than the server replays there, and every
 * square she had filled would land on the wrong grid.
 */
export function useMosaic(
  round: DealtRound<MosaicConfigFromRound>,
  onFinished: () => void,
): Mosaic {
  const [state, dispatch] = useReducer(reduceRound, round.seed, (seed) =>
    start(seed, round.config.size, round.config.pictureKey, round.config.levels, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = state.state.status !== 'playing'

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The
        // round is not spent, so it can still be finished from another device.
        if (!told) return undefined

        return api('/api/plays/mosaic', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She finished the
      // picture either way.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  const send = useCallback(
    (action: MosaicAction) => {
      const now = Date.now()

      dispatch({ action, now })
      // Never earlier than the move before it.
      moves.record(action, Math.max(0, now - state.state.startedAt))
    },
    [moves, state.state.startedAt],
  )

  return {
    view: toMosaicView(state.state),
    set: useCallback(
      (cell: number, next: CellState) => {
        send({ type: 'set', cell, state: next })
      },
      [send],
    ),
    /*
     * Sent to the server like any other move, and it has to be.
     *
     * A reset the server never heard of would leave its grid holding squares
     * hers no longer has. It is also the move that made a 15×15 need a bigger
     * ceiling than four hundred: one press means re-entering the whole grid.
     */
    clear: useCallback(() => {
      send({ type: 'clear' })
    }, [send]),
    again: onFinished,
    finished,
    milestone: award.milestone,
  }
}
